import { PaywallResult, Plan } from '@accedo/one-plugin-sdk';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Status } from '#/components/Status';
import { SignupStepIndicator } from '#/components/StepIndicator/SignupStepIndicator';
import { AppStatus } from '#/service/constants';
import { Path } from '#/service/routes';
import { noop, remify } from '#/service/utils';
import { authActions } from '#/store/auth';

import { Logo, LogoContainer } from '../Logo';
import { PlanPickerProps } from './props';
import Payment from './steps/Payment/Payment';
import PlanSelection from './steps/PlanSelection/PlanSelection';
import { Step } from './types';

const defaultSteps: Step[] = [
  {
    header: 'iap.selectYourPlan',
    Component: PlanSelection,
  },
  {
    header: 'iap.setUpYourPayment',
    Component: Payment,
  },
];

const PlanPickerContainer = styled.div`
  display: grid;
  justify-items: center;
  align-content: start;
  row-gap: ${remify(45)};
  text-align: left;
  margin: ${remify(80)} ${remify(30)} ${remify(75)};
`;

const PlanPickerHeader = styled.h2`
  font: var(--fontStyleHeadline);
  color: var(--pageHeadingForeground);
  margin: 0;
  text-align: center;
  max-width: 80vw;
`;

const PlanPickerStepContainer = styled.div`
  width: 100%;
  max-width: ${remify(450)};
`;

const PlanPicker = ({
  plans,
  actions,
  steps = defaultSteps,
  hasError,
  customParameters,
  onResult = noop,
}: PlanPickerProps) => {
  const [plan, setPlan] = useState<Plan>();
  const [payment, setPayment] = useState<{
    result: boolean;
    isExternal?: boolean;
  }>();
  const [step, setStep] = useState(0);
  const { pathname } = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!plans.length) {
      actions.handleError({
        title: t('iap.emptyState.title'),
        message: t('iap.emptyState.message'),
      });
    }
  }, [plans.length, actions]);

  useEffect(() => {
    return () => {
      // When closing plan picker reset entitlements
      authActions.setPurchaseEntitlements(undefined);
    };
  }, []);

  // scroll all parents, including the full page modal to the top on step change
  useEffect(() => {
    let element = containerRef.current?.parentElement;

    while (element) {
      element.scrollTop = 0;
      element = element.parentElement;
    }
  }, [step]);

  if (hasError || !plans.length || step >= steps.length) {
    return null;
  }

  const handlePlanChange = (selectedPlan: Plan) => {
    if (customParameters?.vendorTag) {
      selectedPlan.customParameters = {
        vendorTag: customParameters.vendorTag,
      };
    }

    setPlan(selectedPlan);
  };

  if (payment) {
    if (payment.isExternal) {
      return (
        <Status
          onDismiss={actions.handleClose}
          status={AppStatus.Success}
          statusMessage={t('web.iap.external.success.message')}
          statusTitle={t('iap.success.title')}
        />
      );
    }

    return (
      <Status
        dismissMessage={t('iap.success.button')}
        onDismiss={actions.handleClose}
        status={AppStatus.Success}
        statusMessage={t('iap.success.message')}
        statusTitle={t('iap.success.title')}
      />
    );
  }

  const { header, Component } = steps[step];
  const isOnRegistrationPage = pathname === Path.Registration;
  // `step` is 0-based, but the indicator's `currentStep` is 1-based.
  // and we need to add 1 because the registration step is already done.
  const indicatorCurrentStep = step + 2;

  return (
    <>
      {!customParameters?.vendorTag && (
        <LogoContainer>
          <Logo />
        </LogoContainer>
      )}
      <PlanPickerContainer ref={containerRef}>
        {isOnRegistrationPage && (
          <SignupStepIndicator currentStep={indicatorCurrentStep} />
        )}
        <PlanPickerHeader>{t(header)}</PlanPickerHeader>
        <PlanPickerStepContainer>
          <Component
            actions={actions}
            customParameters={customParameters}
            nextStep={() => setStep(step + 1)}
            onPaymentChange={({ isSuccessful, isExternal }) => {
              setPayment({ result: isSuccessful, isExternal: isExternal });
              onResult(
                isSuccessful ? PaywallResult.Payed : PaywallResult.Aborted
              );
            }}
            onPlanChange={handlePlanChange}
            plans={plans}
            previousStep={() => setStep(step - 1)}
            selectedPlan={plan}
          />
        </PlanPickerStepContainer>
      </PlanPickerContainer>
    </>
  );
};

PlanPicker.displayName = 'PlanPicker';

export default PlanPicker;
